import React from "react";
import {
  Box,
  Text,
  Badge,
  VStack,
  HStack,
  Flex,
  SimpleGrid,
  Image,
} from "@chakra-ui/react";

const ApiSecurityCompliance = () => {
  
  const CheckmarkIcon = ({ variant = "green" }) => {
    const isWhite = variant === "white";
    const isBlue = variant === "blue";
    const bg = isWhite ? "transparent" : isBlue ? "transparent" : "#10B981";
    const stroke = isWhite ? "white" : isBlue ? "#93C5FD" : "white";
    return (
      <Box
        w="16px"
        h="16px"
        borderRadius="50%"
        bg={bg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.33329 2.5L3.74996 7.08333L1.66663 5"
            stroke={stroke}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    );
  };


  const securityFeatures = [
    {
      icon: "assets/ndpr.svg",
      title: "NDPR Compliant",
      description:
        "Full Nigeria Data Protection Regulation compliance. We protect your data with the highest standards and ensure all processing meets regulatory requirements.",
      bullets: [
        "Data encryption at rest and in transit",
        "Regular compliance audits",
        "Data residency in Nigeria",
      ],
    },
    {
      icon: "assets/grade.svg", 
      title: "Bank-grade Encryption",
      description:
        "Advanced security and fraud detection systems protect every transaction. Multi-layer security ensures your data and funds are always safe.",
      bullets: [
        "256-bit SSL encryption",
        "Real-time fraud monitoring",
        "PCI DSS Level 1 compliant",
      ],
    },
    {
      icon: "assets/licensed.svg",
      title: "Licensed Partners",
      description:
        "Work with regulated financial institutions licensed by the Central Bank of Nigeria. Your funds are held in secure, segregated accounts.",
      bullets: [
        "CBN-licensed partners",
        "Segregated client accounts",
        "Regulatory oversight",
      ],
    },
    {
      icon: "assets/regular.svg",
      title: "Regular Security Audits",
      description:
        "Continuous monitoring and updates ensure our platform remains secure. Third-party security audits validate our security posture.",
      bullets: [
        "Quarterly security audits",
        "Penetration testing",
        "24/7 security monitoring",
      ],
    },
  ];

  const metrics = [
    { value: "99.9%", label: "Uptime SLA" },
    { value: "256-bit", label: "SSL Encryption" },
    { value: "24/7", label: "Security Monitoring" },
  ];

  return (
    <Box
      bg="#0F172B"
      w="100vw"
      position="relative"
      left="50%"
      right="50%"
      ml="-50vw"
    >
      <Box
        w="100%"
        maxW="1326px"
        mx="auto"
        py={{ base: "60px", md: "100px" }}
        px={{ base: "20px", md: "80px", lg: "120px" }}
      >
      <VStack spacing="60px" align="center" w="100%">
        {/* Top Section */}
        <VStack spacing="24px" align="center" w="100%" maxW="1200px">
          <Badge
            display="flex"
            alignItems="center"
            gap="8px"
            w="fit-content"
            bg="#2D3442"
            color="#FFFFFF"
            px="16px"
            py="6px"
            border="1px solid rgba(255, 255, 255, 0.2)"
            borderRadius="20px"
            fontSize="13px"
            fontWeight={600}
          >
            <Image
              src="/assets/secure.svg"
              alt="shield"
              w="16px"
              h="16px"
            />
            Security & Compliance
          </Badge>

          <Text
            fontSize={{ base: "32px", md: "48px", lg: "56px" }}
            fontWeight={600}
            textAlign="center"
            color="#FFFFFF"
            lineHeight="1.2"
          >
            Secure, Compliant, and Reliable
          </Text>

          <Text
            fontSize={{ base: "16px", md: "18px", lg: "20px" }}
            textAlign="center"
            color="rgba(255, 255, 255, 0.8)"
            maxW="800px"
            lineHeight="1.6"
          >
            Your trust is our foundation. BizFlex APIs are built with bank-grade
            security and backed by licensed financial partners.
          </Text>
        </VStack>

        {/* Security Features Grid */}
        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={{ base: "32px", md: "40px" }}
          w="100%"
          maxW="1200px"
        >
          {securityFeatures.map((feature, index) => (
            <Box
              key={feature.title}
              bg="#2D3442"
              borderRadius="24px"
              p={{ base: "24px", md: "32px" }}
              border="1px solid rgba(255, 255, 255, 0.1)"
              _hover={{
                bg: "rgba(255, 255, 255, 0.08)",
                borderColor: "rgba(255, 255, 255, 0.2)",
                transform: "translateY(-4px)",
                transition: "all 0.3s ease",
              }}
              transition="all 0.3s ease"
            >
              <VStack align="flex-start" spacing="20px" w="100%">
                {/* Icon */}
                <Image
                  src={feature.icon}
                  alt={feature.title}
                  w="48px"
                  h="48px"
                />

                {/* Title */}
                <Text
                  fontSize={{ base: "20px", md: "24px" }}
                  fontWeight={700}
                  color="#FFFFFF"
                >
                  {feature.title}
                </Text>

                {/* Description */}
                <Text
                  fontSize={{ base: "14px", md: "16px" }}
                  color="rgba(255, 255, 255, 0.7)"
                  lineHeight="1.6"
                >
                  {feature.description}
                </Text>

                {/* Bullet Points: first column (0,2) white tick, second column (1,3) green (current) */}
                <VStack align="flex-start" spacing="18px" w="100%">
                  {feature.bullets.map((bullet, bulletIndex) => (
                    <HStack key={bulletIndex} spacing="12px" align="flex-start">
                      <CheckmarkIcon variant={index === 0 || index === 2 ? "white" : "blue"} />
                      <Text
                        fontSize={{ base: "13px", md: "14px" }}
                        color="rgba(255, 255, 255, 0.7)"
                        lineHeight="1.5"
                      >
                        {bullet}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>

        {/* Metrics Section - in a box */}
        <Box
          w="100%"
          maxW="1200px"
          bg="#2D3442"
          borderRadius="20px"
          border="1px solid rgba(255, 255, 255, 0.1)"
          p={{ base: "40px", md: "56px" }}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            gap={{ base: "40px", md: "80px" }}
            w="100%"
          >
            {metrics.map((metric, index) => (
              <VStack key={index} spacing="12px" align="center" flex={1}>
                <Text
                  fontSize={{ base: "40px", md: "56px", lg: "64px" }}
                  fontWeight={700}
                  color={metric.value === "256-bit" ? "#93C5FD" : "#FFFFFF"}
                  textAlign="center"
                  lineHeight="1.2"
                >
                  {metric.value}
                </Text>
                <Text
                  fontSize={{ base: "15px", md: "17px" }}
                  fontWeight={400}
                  color="rgba(255, 255, 255, 0.6)"
                  textAlign="center"
                >
                  {metric.label}
                </Text>
              </VStack>
            ))}
          </Flex>
        </Box>
      </VStack>
      </Box>
    </Box>
  );
};

export default ApiSecurityCompliance;

